
<div class="page-body">
    <h1 data-aos="fade-down" data-aos-duration="800">Together Apart: Redefining Remote Co-Dining Interactions</h1>
    <div class="info">
        <p><strong>Context</strong><br>HCI Research @ iStudio Labs </p>
        <p><strong>Team</strong><br>Meara Donovan, Eliza Wallace, Alaa Nousir, Dr. Sara Nabil (Supv)</p>
        <p><strong>Role</strong><br>Lead Researcher</p>
        <p><strong>Tools</strong><br>Blender, Creality CR-10, Adobe Suite</p>
    </div>
    <img class="process" src="assets/projects/togetherapart/togetherapart.png">
    <h2>Brief</h2>
    <div class="brief">
        <img src="assets/projects/togetherapart/brief.jpg">
    </div>
    <div class="category">
        <h3 data-aos-anchor=".brief">Listen.</h3>
        <h4>Pain point</h4>
        <p>The COVID-19 pandemic has brought various challenges, including heightened stress and health-anxiety, employment and financial instability, isolation, and quarantine inside the home. Dining is a chance to connect with friends and family, but during the isolation of the COVID-19 pandemic, physical co-dining (the act of physically dining together) becomes less accessible.  </p>
        <blockquote>Canadians are reporting
            worsening mental health
            due to the pandemic,
            with 41% of the population reporting heightened
            feelings of anxiety. [Angus Reid Institute]</blockquote>
        <img class="centered" src="assets/projects/togetherapart/table.jpg">
        <h4>Designing interactive, social, interiors</h4>
        <p>We explored the uses of smart materials for promoting tangible connectivity between physically separated individuals, aiming to increase social health. We then engaged 10 participants in a co-designing study before building 5 prototypes of interactive dining artifacts to deploy in their homes in a second user study 'in-the-wild'. </p>
        <p class="block"></p>
    </div>

    <div class="category">
    <h3>Reshape.</h3>
        <h4>Reimagining Interaction.</h4>
        <p>Interactive spaces offer opportunities to create
            immersive enriched user experiences through tangible
            and tactile interactions using smart materials. This includes the design of different 
            interactive dining spaces, which examines the interactions between users and 
            food and, dining room atmosphere and culture. In many cases, positive experiences of
            dining are influenced by social factors, such as sharing
            food or eating with family or friends. A subset of research in the area is dedicated to the
            remote co-dining experience, or the experience of
            dining with others in a physically distanced situation.
        </p>

        <p>In this design research, we look into the potentials
            of interactive everyday things designed with
            crafted materiality that augments people's isolated
            domestic dining experience to feel 'Together Apart'. Using dining as a tool to enhance social connectivity,
            we hypothesized that an interactive dining
            system would decrease feelings of isolation
            in individuals living apart from their families.
            Our research hopes to provide insight
            on the domestic dining experience during
            a pandemic and how an interactive dining
            room may influence a socially distanced
            dining experience.</p>

        <p class="block"></p>
        <h4>Research Through Design Process</h4>
        <p>Our goals in this HCI research project were to address the issues of increased anxiety and feelings of isolation, especially during the COVID-19 pandemic, by seeking to connect family and friends through a remote co-dining experience. We conducted a Research Through Design process so that our artifacts build upon real-life dining experiences. This process includes the problem discussion, an ideation study, ideation sessions based on findings of the study, prototyping, deploying the prototypes in the wild, and reflecting on the findings of the deployment. </p>
        <img class="process" src="assets/projects/togetherapart/process_colored-01.jpg">
        <h4>Co-design and Ideation</h4>
        <p>We wanted our designs to reflect our study participants' challenges, preferences, and dining habits while being isolated from their friends and family members.</p>
        <p>interviews were transcribed into 13200 words and analyzed into over two hundred insightful quotes during the thematic analysis. Seven overarching themes were formed from the interviews and these were then further refined in an iterative process (done separately by 5
            researchers in our team) into 3 major themes and goals.</p>
        <img class="centered" src="assets/projects/togetherapart/thematicanalysis.jpg">
        <p class="centered"><i>The theme coding process of thematic analysis</i></p>
        <p class="block"></p>
        <p><b>We found three overarching themes in this study:</b></p>
        <p><strong>1.  Emphasize balancing communication and    
            personal time: </strong> Participants valued the prescence of others at the dining table and eating together is a form of maintaining relationships. Sometimes, participants did neeed alone time to relax.</p>
        <blockquote>💬 "[Dining] kinda brings people together...[it] lets me check in with my friends and my roommates [and know] what's going on."</blockquote>
        <p class="block-sm"></p>
        <p><strong>2. Connection to the outside environment:</strong> Participants’ aesthetic preferences in the dining room such as ambient music/ background noise, dim light, organic shapes as well as plants and flowers reflect biophilic ideology (that design should emulate nature).  People also desire to be connected to activity or ‘bustle’ happening outside of the room.</p>
        <blockquote>💬"Some candles, some ambient music, just dim lights."</blockquote>
        <p class="block-sm"></p>
        <p><strong>3. Combating unhealthy habits formed in isolation:</strong> Isolation during the pandemic year has led to increased loneliness and poor dining-habits.</p>
        <blockquote>💬  "You do lose track of time, [it’s] easy to work away and blow through meal times." </blockquote>
        <p class="block"></p>   
        <h4>Applying Slow Design and Biophillic Design techniques </h4>
        <p>With feedback from interviews and
            research, we came up with three designs
            during the ideation process: a connectivity
            candle, a pressure sensing placemat, and
            a yarn concert wall art piece.
            To explore the three design themes,
            we apply design methodologies such
            as Slow Design (a concept that stresses interacting with products at the right speed and with reflection) by combining multiple
            functionalities within one product, for
            instance, decoration, communication, and
            interaction, with the goal of stimulating
            greater reflection and time spent with
            the items.</p>
        <p>We also create a calmer method of
            communication that does not detract from
            the dining experience itself by designing
            items that function in the periphery of a user's focus. For example, in the use case of a person video calling another person during dinner, there will be tension between focusing on food and interacting with the video chat interface, whereas flickering candlelights do not take up much focus.</p>
            <img class="process" src="assets/projects/togetherapart/concepts.png">
        </div>

    <div class="category">
        <h3>Create.</h3>
        <h4>Prototype: blending technology into a 'traditional interface'</h4>
        <p>In order to incorporate 
            concepts such as presence 
            and interactivity we 
            began experimenting 
            with smart materials such 
            as yarn, single-board 
            computers, 3D printing, 
            and microcontrollers. 
            The two pronged 
            connectivity candle 
            explores the concept of 
            indirectly communicating 
            presence through a 
            sensory detection system. 
            On the other hand, the 
            Yarn Concert allows users 
            to interact with the yarn 
            wall art and produce 
            a soundscape. Finally, 
            the Playful Placemat is 
            designed to emulate 
            food sharing and mindful 
            eating, as LED lights on 
            a crochet mat respond to 
            the pressure of the plates 
            placed on itself. This 
            was contrasted with our 
            exploration of traditional 
            materials such as paint and 
            yarn in the exterior design 
            as participants found them 
            soothing and homely. </p>
            
        <img class="process" src="assets/projects/togetherapart/materiality.jpg">

        <h4>Learning from doing </h4>
        <p>We learned about the construction methods during multiple iterations of implementations. For instance, the connectivity candle required learning the intricacies of 3D modelling, 3D printing, implementing a simple web service for the communication system, and exploring painting techniques.</p>

        <img class="process" src="assets/projects/togetherapart/implementation.jpg">
        <h4>'In the Wild' prototype testing</h4>
        <p>Results from both user interviews/tests reveal that
            ambient communication and slow technology
            can be used to communicate without the
            the pressure of forcing conversation. On the other
            hand, slow interaction helped generate deeper
            reflection about people’s relationships and
            allowed them to reach out to family and friends
            about meaningful topics. Additionally, items
            that blend into the environment and make
            metaphors to the outdoors make people feel
            comfortable. In reaction to the three design
            artifacts, participants are eating healthier,
            reframing dining as more of an important event,
            talking about food with others and spending
            time with food as opposed to digital screens.
            </p>
        <a href="assets/Together_Apart.pdf" target="_blank" >Feel free to take a look at our pictorial paper for all the findings. </a>

        <div class="three-grid">
            <img class="process" src="assets/projects/togetherapart/candlegif.gif">
            <img class="process" src="assets/projects/togetherapart/yarnconcertgif.gif">
            <img class="process" src="assets/projects/togetherapart/placematgif.gif">
        </div>
    </div>

    <div class="category">
        <h3>Reflections.</h3>
        <p>All three artefacts were
            considered “conversation pieces”; they
            provoked discussion, provided another avenue
            for users within a relationship to connect, and
            encouraged users to reflect upon their dining
            environment. The pieces themselves provided
            company and “filled the space” by appealing
            to the senses through light, sound, and touch.
            Users went through the stages of curiosity,
            exploration, familiarity, and preference while
            interacting with the artefacts. The biophilic
            design of our artefacts was well-received
            by the users, as they spend whole days on
            screens due to COVID-19. Overall, the pieces
            had an emotional impact on users, and
            rather than being a part of the background,
            encouraged a more mindful dining experience. </p>
        <img class="process" src="assets/projects/togetherapart/banner.jpg">
        <p class="block"></p>
        <h4>The Future</h4>
        <p>Further design iterations should focus on
            longevity (improved power source and more
            sustained interaction) and inter-artifact
            connection. For instance, Yarn Concert could
            connect wirelessly to its partner piece, further
            bridging the physical space between their
            users. </p>
        <p>Moreover, future user studies could
            be evaluated in other environments such as
            long-term care homes, between long-distance
            partners, or in other contexts where loved ones are living separately. Specifically, we
            discovered that these artefacts have high
            potential for use with senior citizens where
            tangible interaction, crafted elements and
            remembrance are valued more than short-term
            mass-produced gadgets. Participants noted that
            the ease of use and lack of visible technology
            provide interactive artefacts with increased
            accessibility and inclusivity to participants
            of different ages.</p>
    </div>
    <!-- video link -->
    <!-- link to peices  -->
    <a class="next" routerLink="/arblockbot">Next work -></a>
</div>
